/**
 * The list grid's URL contract: `?page=` and `?pageSize=`, read by every server-paged loader and
 * written by `PagedTable`'s pager. Page size is a URL param so a "Rows per page" change refetches
 * the right slice instead of re-slicing whatever page the server already sent.
 */

/** `TablePagination`'s own option list (`packages/theme/src/components/table/pagination.tsx`). */
export const PAGE_SIZES = [5, 10, 15, 20, 25] as const;
export const DEFAULT_PAGE_SIZE = 10;

export function readPaging(params: URLSearchParams): { page: number; pageSize: number } {
  const page = Math.max(1, Number(params.get("page")) || 1);
  const size = Number(params.get("pageSize"));
  return { page, pageSize: (PAGE_SIZES as readonly number[]).includes(size) ? size : DEFAULT_PAGE_SIZE };
}

/**
 * The All / Active / Inactive choice every list of active-flagged records offers. **Active is the
 * default** (project owner, 2026-09-29): no param means Active, `?all=1` is All, `?inactive=1` is
 * Inactive. A bookmarked `?activeOnly=1` (row links used to carry it) still reads as Active; nothing
 * generates it any more. The services take `activeOnly` and nothing else, so an Inactive
 * view is read as the whole set and filtered on `isActive` by its loader.
 */
export type ActiveView = "all" | "active" | "inactive";

export function readActiveView(params: URLSearchParams): ActiveView {
  if (params.get("inactive") === "1") return "inactive";
  if (params.get("all") === "1") return "all";
  return "active";
}

/** The params for `hrefWith`, so switching views drops the others and keeps the rest. */
export function activeViewParams(view: ActiveView): Record<string, string | undefined> {
  return { activeOnly: undefined, all: view === "all" ? "1" : undefined, inactive: view === "inactive" ? "1" : undefined, page: undefined };
}

/**
 * `path?query` with `over` applied on top of the current params: a value sets, `undefined` deletes.
 * Every other param (the page size, the other filters) survives, which is the point.
 */
export function hrefWith(path: string, params: URLSearchParams, over: Record<string, string | undefined>): string {
  const next = new URLSearchParams(params);
  for (const [key, value] of Object.entries(over)) {
    if (value) next.set(key, value);
    else next.delete(key);
  }
  const query = next.toString();
  return query ? `${path}?${query}` : path;
}
